import { CommonModule } from '@angular/common';
import { Component, effect, input, signal, computed } from '@angular/core';

import { ISalesPerformanceResponse } from '@shared/interfaces/sales-performence.interface';

import { ChartModule } from 'primeng/chart';
import { ProgressSpinner } from 'primeng/progressspinner';

@Component({
    selector: 'app-monthly-sales-chart',
    imports: [CommonModule, ChartModule, ProgressSpinner],
    templateUrl: './monthly-sales-chart.component.html',
    styleUrl: './monthly-sales-chart.component.scss'
})
export class MonthlySalesChartComponent {
    loading = input<boolean>(false);
    monthlyData = input<ISalesPerformanceResponse['thisYear']['monthly']>();

    readonly totalReservations = computed(() => {
        const data = this.monthlyData();
        if (!data) return 0;
        return data.reduce((sum, item) => sum + (item.reservation ?? 0), 0);
    });

    readonly totalSeats = computed(() => {
        const data = this.monthlyData();
        if (!data) return 0;
        return data.reduce((sum, item) => sum + (item.seat ?? 0), 0);
    });

    readonly totalCancels = computed(() => {
        const data = this.monthlyData();
        if (!data) return 0;
        return data.reduce((sum, item) => sum + (item.cancel ?? 0), 0);
    });

    chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            }
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { size: 10 } }
            },
            y: {
                beginAtZero: true,
                grid: { color: '#edf0f5' },
                ticks: { font: { size: 10 } }
            }
        },
        elements: {
            line: {
                tension: 0.4
            },
            point: {
                radius: 3,
                hoverRadius: 5
            }
        }
    };

    chartData = signal({
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [
            {
                label: 'Seat',
                data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                borderColor: '#3974df',
                backgroundColor: 'rgba(57, 116, 223, 0.1)',
                fill: true,
                borderWidth: 3
            },
            {
                label: 'Reservation',
                data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                borderColor: '#49a765',
                backgroundColor: 'transparent',
                fill: false,
                borderWidth: 2
            },
            {
                label: 'Cancel',
                data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                borderColor: '#e05a67',
                backgroundColor: 'transparent',
                fill: false,
                borderWidth: 2
            }
        ]
    });

    constructor() {
        effect(() => {
            this.updateChartData();
        });
    }

    updateChartData() {
        const monthly = this.monthlyData();
        if (!monthly) return;

        this.chartData.update((value) => ({
            ...value,
            datasets: [
                {
                    ...value.datasets[0],
                    data: monthly.map((item) => item.seat ?? 0)
                },
                {
                    ...value.datasets[1],
                    data: monthly.map((item) => item.reservation ?? 0)
                },
                {
                    ...value.datasets[2],
                    data: monthly.map((item) => item.cancel ?? 0)
                }
            ]
        }));
    }
}
